/**
 * Layout Component - Navigatie en structuur
 */

import { ReactNode, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../services/auth.service';
import apiFetch from '../services/api';
import { useAuth } from '../context/AuthContext';
import './Layout.css';

interface LayoutProps {
  children: ReactNode;
}

interface VoorraadCategorie {
  id: number;
  code: string;
  naam: string;
  volgorde: number;
  actief: boolean;
}

const Layout = ({ children }: LayoutProps) => {
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [voorraadCategorieen, setVoorraadCategorieen] = useState<VoorraadCategorie[]>([]);
  const { hasPermission, user, loading } = useAuth();

  useEffect(() => {
    const loadCategorieen = async () => {
      try {
        const data = await apiFetch('/api/v1/voorraad/categorieen');
        setVoorraadCategorieen(data);
      } catch (err) {
        console.error('Error loading voorraad categorieen:', err);
      }
    };
    loadCategorieen();
  }, []);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    authService.logout();
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (mobileMenuOpen) {
      setActiveDropdown(null);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const toggleDropdown = (name: string) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  // If auth is still loading OR we have a token but no user loaded yet, show loading screen
  if (loading || (authService.isAuthenticated() && !user)) {
    return (
      <div className="layout">
        <div className="status-bar-spacer" />
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '1.1rem',
          color: '#666'
        }}>
          Laden gebruikersgegevens...
        </div>
      </div>
    );
  }

    return (
      <div className="layout">
        <div className="status-bar-spacer" />
        <nav className="navbar">
          <Link to="/" className="navbar-brand">
            <h1>
              <span style={{ color: '#e06c00' }}>Buro</span>
              <span style={{ color: '#1f6fd0' }}>42</span>
            </h1>
          </Link>

          <button className="hamburger-menu" onClick={toggleMobileMenu} aria-label="Menu">
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
          </button>

          <div className={`navbar-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            {/* Clean debug code */}
            
            <div className={`navbar-dropdown ${activeDropdown === 'relaties' ? 'active' : ''}`}>
              <span className="navbar-item" onClick={() => toggleDropdown('relaties')}>Relaties</span>
              <div className="dropdown-content">
                <Link to="/relations?type=klant" onClick={closeMobileMenu}>Klanten</Link>
                <Link to="/relations?type=leverancier" onClick={closeMobileMenu}>Leveranciers</Link>
                <Link to="/relations?type=zzp" onClick={closeMobileMenu}>ZZP'ers</Link>
              </div>
            </div>
            <div className={`navbar-dropdown ${activeDropdown === 'personeel' ? 'active' : ''}`}>
              <span className="navbar-item" onClick={() => toggleDropdown('personeel')}>Personeel</span>
              <div className="dropdown-content">
                <Link to="/werknemers" onClick={closeMobileMenu}>Werknemers</Link>
                <Link to="/urenregistratie" onClick={closeMobileMenu}>Urenregistratie</Link>
                <Link to="/kilometerregistratie" onClick={closeMobileMenu}>Kilometerregistratie</Link>
                <span className="disabled">Planning/Resources</span>
              </div>
            </div>
            <div className={`navbar-dropdown ${activeDropdown === 'boten' ? 'active' : ''}`}>
              <span className="navbar-item" onClick={() => toggleDropdown('boten')}>Boten</span>
              <div className="dropdown-content">
                <Link to="/boot-modellen" onClick={closeMobileMenu}>Boot Modellen</Link>
                <Link to="/boten" onClick={closeMobileMenu}>Klant Boten</Link>
                <Link to="/onderdelen" onClick={closeMobileMenu}>Gemonteerde Onderdelen</Link>
              </div>
            </div>
            <div className={`navbar-dropdown ${activeDropdown === 'producten' ? 'active' : ''}`}>
              <span className="navbar-item" onClick={() => toggleDropdown('producten')}>Producten</span>
              <div className="dropdown-content">
                <Link to="/voorraad" onClick={closeMobileMenu}>Alle Producten</Link>
                <div className="dropdown-divider"></div>
                {/* Elke categorie linkt naar de catalogus. Scheepsonderdelen (80000)
                    ging naar /onderdelen, maar dat toont gemonteerde onderdelen van
                    boten uit een andere tabel - die staat nu onder het menu Boten.
                    Aangepast 25-09-2026. */}
                {voorraadCategorieen.map(categorie => (
                  <Link
                    key={categorie.id}
                    to={`/voorraad?categorie=${categorie.code}`}
                    onClick={closeMobileMenu}
                  >
                    {categorie.naam}
                  </Link>
                ))}
              </div>
            </div>
            {hasPermission('offertes_bekijken') && (
              <div className={`navbar-dropdown ${activeDropdown === 'offertes' ? 'active' : ''}`}>
                <span className="navbar-item" onClick={() => toggleDropdown('offertes')}>Offertes</span>
                <div className="dropdown-content">
                  <Link to="/offertes" onClick={closeMobileMenu}>Offerte Overzicht</Link>
                  <Link to="/offertes/new" onClick={closeMobileMenu}>Nieuwe Offerte</Link>
                </div>
              </div>
            )}
            <div className={`navbar-dropdown ${activeDropdown === 'werkorders' ? 'active' : ''}`}>
              <span className="navbar-item" onClick={() => toggleDropdown('werkorders')}>Werkorders</span>
              <div className="dropdown-content">
                <Link to="/werkorders" onClick={closeMobileMenu}>Werkorder Overzicht</Link>
                <Link to="/werkorders/new" onClick={closeMobileMenu}>Nieuwe Werkorder</Link>
                <span className="disabled">Planning</span>
              </div>
            </div>
            {hasPermission('facturen_bekijken') && (
              <div className={`navbar-dropdown ${activeDropdown === 'facturen' ? 'active' : ''}`}>
                <span className="navbar-item" onClick={() => toggleDropdown('facturen')}>Facturen</span>
                <div className="dropdown-content">
                  <div className="dropdown-section-title">Inkoop</div>
                  <Link to="/bestelpool" onClick={closeMobileMenu}>Bestelpool</Link>
                  <Link to="/bestellingen" onClick={closeMobileMenu}>Bestellingen</Link>
                  <span className="disabled">Inkoopfacturen Overzicht</span>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-section-title">Verkoop</div>
                  <Link to="/facturen/new" onClick={closeMobileMenu}>Nieuwe Verkoopfactuur</Link>
                  <Link to="/facturen" onClick={closeMobileMenu}>Verkoopfacturen Overzicht</Link>
                </div>
              </div>
            )}
            {hasPermission('boekhouding_bekijken') && (
              <Link to="/boekhouding/dashboard" className="navbar-item" onClick={closeMobileMenu}>Boekhouding</Link>
            )}
            {hasPermission('instellingen_bekijken') && (
              <div className={`navbar-dropdown ${activeDropdown === 'instellingen' ? 'active' : ''}`}>
                <span className="navbar-item" onClick={() => toggleDropdown('instellingen')}>Instellingen</span>
                <div className="dropdown-content">
                  <Link to="/settings/bedrijfsgegevens" onClick={closeMobileMenu}>Bedrijfsgegevens</Link>
                  <Link to="/settings/email" onClick={closeMobileMenu}>Email / SMTP</Link>
                  <Link to="/settings/algemeen" onClick={closeMobileMenu}>Algemene Instellingen</Link>
                  <Link to="/settings/rollen" onClick={closeMobileMenu}>Rollen & Permissies</Link>
                  <Link to="/settings/database-export" onClick={closeMobileMenu}>Database Export</Link>
                  <Link to="/settings/todo" onClick={closeMobileMenu}>ToDo Lijst</Link>
                  <div className="dropdown-divider"></div>
                  <Link to="/help" onClick={closeMobileMenu}>Help</Link>
                </div>
              </div>
            )}
          </div>

          <div className="navbar-end">
            <button onClick={handleLogout} className="btn-logout">
              Uitloggen
            </button>
          </div>
        </nav>

        {/* Mobile menu overlay */}
        {mobileMenuOpen && (
          <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>
        )}

        <main className="main-content">
          {children}
        </main>

        {/* Uitloggen bevestigingsmodal */}
        {showLogoutModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}>
            <div style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '8px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              textAlign: 'center'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#1a1a1a' }}>
                Uitloggen
              </h3>
              <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                Weet je zeker dat je wilt uitloggen?
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button
                  onClick={() => setShowLogoutModal(false)}
                  style={{
                    padding: '0.6rem 1.5rem',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: 500
                  }}
                >
                  Annuleren
                </button>
                <button
                  onClick={confirmLogout}
                  style={{
                    padding: '0.6rem 1.5rem',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: 500
                  }}
                >
                  Uitloggen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  export default Layout;
